import { useEffect, useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Box, IconButton } from '@mui/material';
import {
  AVAILABILITY_DOT_COLOR,
  AVAILABILITY_STATUS,
} from 'constants/availabilityStatus';
import { useTranslation } from 'react-i18next';
import AvailabilityDropdown from './AvailabilityDropdown';

const ARIA_LABEL_KEYS = {
  [AVAILABILITY_STATUS.AVAILABLE]: 'menu.availability_available',
  [AVAILABILITY_STATUS.UNAVAILABLE_TODAY]:
    'menu.availability_unavailable_today',
  [AVAILABILITY_STATUS.UNAVAILABLE_INDEFINITE]:
    'menu.availability_unavailable_indefinite',
};

export default function AvailabilityControl({
  value = AVAILABILITY_STATUS.AVAILABLE,
  onChange,
  disabled = false,
  entityName,
}) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [internalValue, setInternalValue] = useState(value);
  const open = Boolean(anchorEl);

  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  const handleOpen = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (disabled) return;
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event) => {
    event?.stopPropagation?.();
    setAnchorEl(null);
  };

  const handleSelect = (status) => {
    setInternalValue(status);
    setAnchorEl(null);
    if (status !== value) {
      onChange?.(status);
    }
  };

  const stopRowActivation = (event) => {
    event.stopPropagation();
  };

  const ariaLabel = entityName
    ? `${t(
        ARIA_LABEL_KEYS[internalValue] ||
          ARIA_LABEL_KEYS[AVAILABILITY_STATUS.AVAILABLE]
      )}: ${entityName}`
    : t(
        ARIA_LABEL_KEYS[internalValue] ||
          ARIA_LABEL_KEYS[AVAILABILITY_STATUS.AVAILABLE]
      );

  const dotColor =
    AVAILABILITY_DOT_COLOR[internalValue] ||
    AVAILABILITY_DOT_COLOR[AVAILABILITY_STATUS.AVAILABLE];

  return (
    <Box
      component="span"
      onClick={stopRowActivation}
      onMouseDown={stopRowActivation}
      onKeyDown={stopRowActivation}
      sx={{ display: 'inline-flex', flexShrink: 0 }}
    >
      <IconButton
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open ? 'true' : undefined}
        aria-label={ariaLabel}
        onClick={handleOpen}
        sx={{
          width: 56,
          height: 40,
          minWidth: 56,
          borderRadius: '8px',
          border: '1px solid #E5E5E5',
          backgroundColor: '#FFFFFF',
          gap: 0.8,
          p: 0,
          '&:hover': {
            backgroundColor: '#F5F5F5',
          },
          '&.Mui-disabled': {
            opacity: 0.6,
          },
        }}
      >
        <Box
          aria-hidden
          sx={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            backgroundColor: dotColor,
            flexShrink: 0,
          }}
        />
        <KeyboardArrowDownIcon
          sx={{
            fontSize: 16,
            color: '#8C8C8C',
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.15s ease',
          }}
        />
      </IconButton>
      <AvailabilityDropdown
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        value={internalValue}
        onSelect={handleSelect}
      />
    </Box>
  );
}
